/**
 * `properties` endpoint wrappers.
 *
 * Field shapes are derived from `serializeProperty` / `serializePropertyListRow`
 * / `serializeUnit` (refactor/backend/src/modules/properties/serializers.ts) —
 * see src/types/property.ts for the full derivation notes. Route paths and
 * ordering are verified against refactor/backend/src/modules/properties/routes.ts.
 */
import { apiClient, uploadFile } from '../client';
import { PROPERTIES, PROPERTIES_PUBLIC, PROPERTY_IMAGES, UNITS } from './paths';
import type { Paginated } from '../types';
import type {
  Property,
  PropertyCreateRequest,
  PropertyImage,
  PropertyListRow,
  PropertyPatchRequest,
  Unit,
  UnitCreateRequest,
  UnitPatchRequest,
} from '../../types/property';

// ---------------------------------------------------------------------------
// properties/
// ---------------------------------------------------------------------------

/** `GET properties/` — paginated, 20 per page. `status`/`search` are server-side filters. */
export async function listProperties(params?: {
  page?: number | undefined;
  status?: string | undefined;
  search?: string | undefined;
}): Promise<Paginated<PropertyListRow>> {
  const response = await apiClient.get<Paginated<PropertyListRow>>(PROPERTIES, { params });
  return response.data;
}

/** `GET properties/{id}/`. */
export async function getProperty(id: number): Promise<Property> {
  const response = await apiClient.get<Property>(`${PROPERTIES}${id}/`);
  return response.data;
}

/**
 * `POST properties/`. `owner` is forced server-side to the requester and
 * `status` starts at `PENDING_APPROVAL` — neither is settable from here.
 * `total_units` blank units are auto-created by the backend.
 */
export async function createProperty(payload: PropertyCreateRequest): Promise<Property> {
  const response = await apiClient.post<Property>(PROPERTIES, payload);
  return response.data;
}

/** `PATCH properties/{id}/` — partial update. */
export async function updateProperty(
  id: number,
  payload: PropertyPatchRequest,
): Promise<Property> {
  const response = await apiClient.patch<Property>(`${PROPERTIES}${id}/`, payload);
  return response.data;
}

/** `DELETE properties/{id}/` — cascades units/images/leases server-side. */
export async function deleteProperty(id: number): Promise<void> {
  await apiClient.delete(`${PROPERTIES}${id}/`);
}

/** `POST properties/{id}/approve/` — superagent only. */
export async function approveProperty(id: number): Promise<Property> {
  const response = await apiClient.post<Property>(`${PROPERTIES}${id}/approve/`);
  return response.data;
}

/** `POST properties/{id}/reject/` — superagent only. */
export async function rejectProperty(id: number): Promise<Property> {
  const response = await apiClient.post<Property>(`${PROPERTIES}${id}/reject/`);
  return response.data;
}

/**
 * `POST properties/{id}/upload_image/` — manager roles only. `.jpg`/`.jpeg`/
 * `.png`/`.webp` only, 5MB max (enforced server-side; matches Django's
 * `validate_image_size`).
 */
export async function uploadPropertyImage(
  propertyId: number,
  file: File,
  caption?: string,
): Promise<PropertyImage> {
  const formData = new FormData();
  formData.append('image', file);
  if (caption !== undefined && caption !== '') formData.append('caption', caption);
  return uploadFile<PropertyImage>(`${PROPERTIES}${propertyId}/upload_image/`, formData);
}

// ---------------------------------------------------------------------------
// properties/public/ — the one unauthenticated read endpoint
// ---------------------------------------------------------------------------

/** `GET properties/public/` — `status=APPROVED` only, plus search/type/price/bedroom filters. */
export async function listPublicProperties(params?: {
  page?: number | undefined;
  search?: string | undefined;
  property_type?: string | undefined;
  min_price?: number | string | undefined;
  max_price?: number | string | undefined;
  bedrooms?: number | string | undefined;
}): Promise<Paginated<PropertyListRow>> {
  const response = await apiClient.get<Paginated<PropertyListRow>>(PROPERTIES_PUBLIC, { params });
  return response.data;
}

/** `GET properties/public/{id}/` — full `PropertySerializer`, not the list row. */
export async function getPublicProperty(id: number): Promise<Property> {
  const response = await apiClient.get<Property>(`${PROPERTIES_PUBLIC}${id}/`);
  return response.data;
}

// ---------------------------------------------------------------------------
// properties/units/
// ---------------------------------------------------------------------------

/** `GET properties/units/` — scoped to the caller; `property`/`is_vacant` filter. */
export async function listUnits(params?: {
  page?: number | undefined;
  property?: number | undefined;
  is_vacant?: boolean | undefined;
}): Promise<Paginated<Unit>> {
  const response = await apiClient.get<Paginated<Unit>>(UNITS, { params });
  return response.data;
}

/** `GET properties/units/{id}/`. */
export async function getUnit(id: number): Promise<Unit> {
  const response = await apiClient.get<Unit>(`${UNITS}${id}/`);
  return response.data;
}

/** `POST properties/units/` — caller must own (or be assigned to) the target property. */
export async function createUnit(payload: UnitCreateRequest): Promise<Unit> {
  const response = await apiClient.post<Unit>(UNITS, payload);
  return response.data;
}

/** `PATCH properties/units/{id}/` — partial update. */
export async function updateUnit(id: number, payload: UnitPatchRequest): Promise<Unit> {
  const response = await apiClient.patch<Unit>(`${UNITS}${id}/`, payload);
  return response.data;
}

/** `DELETE properties/units/{id}/` — cascades that unit's images/leases server-side. */
export async function deleteUnit(id: number): Promise<void> {
  await apiClient.delete(`${UNITS}${id}/`);
}

// ---------------------------------------------------------------------------
// properties/images/
// ---------------------------------------------------------------------------

/** `DELETE properties/images/{id}/` — manager-only, scoped to the owning property. */
export async function deletePropertyImage(id: number): Promise<void> {
  await apiClient.delete(`${PROPERTY_IMAGES}${id}/`);
}
